import React, { useState, useEffect, useRef } from 'react';
import { useCanvasPanel } from '../../contexts/CanvasPanelContext';
import { ContentBlock, FileBlock, DocumentCitationBlock } from '../../models/Message';
import DownloadIcon from '@mui/icons-material/Download';
import LinkIcon from '@mui/icons-material/Link';
import LockIcon from '@mui/icons-material/Lock';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import PsychologyIcon from '@mui/icons-material/Psychology';
import ImageIcon from '@mui/icons-material/Image';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import filePdf from '../../assets/logos/filePdf.svg';
import fileDocx from '../../assets/logos/fileDocx.svg';
import fileXlsx from '../../assets/logos/fileXlsx.svg';
import fileTxt from '../../assets/logos/fileTxt.svg';
import fileLogo from '../../assets/logos/file.svg';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { styled, useTheme } from '@mui/material/styles';
import { Popover, Box, Typography, Chip, CircularProgress, Skeleton } from '@mui/material';
import { activatable } from '../../utils/a11y';

interface ContentBlockRendererProps {
  blocks: ContentBlock[];
  onFileDownload?: (fileBlock: FileBlock) => void;
  onFileOpen?: (fileBlock: FileBlock) => void;
  onCitationView?: (citation: DocumentCitationBlock) => Promise<void>;
  onCitationDownload?: (citation: DocumentCitationBlock) => Promise<void>;
  onMentionClick?: (userId: string) => void;
  userMap?: Record<string, { name: string; email: string; userType: 'user' | 'agent' }>;
  loadingFileName?: string | null;
  /** True while the streaming hook is actively receiving block events */
  isStreaming?: boolean;
  /** Brand color for links, icons, and accents (defaults to primary blue) */
  accentColor?: string;
  /** Resolve a storage URL to a signed/accessible URL for image blocks */
  onResolveImageUrl?: (url: string) => Promise<string>;
  /**
   * True when these blocks render on a filled bubble (the user's own message)
   * rather than a neutral surface. Mentions then inherit the bubble's text
   * color instead of the accent token, which cannot meet contrast on every
   * bubble color. See MentionSpan.
   */
  onColoredSurface?: boolean;
}

// Internal type for blocks during streaming (adds transient metadata fields)
type StreamableBlock = ContentBlock & { _streaming?: boolean; _removed?: boolean };

// The previous hardcoded #f57c00 was commented "better contrast on both white
// and blue backgrounds"; measured, it was 2.70:1 on white and 2.44:1 on the
// blue user bubble — it failed on both. No single accent clears 4.5:1 against
// every bubble color across the four themes (the dark user bubble admits only
// white), so the color is a per-theme token and is dropped entirely on colored
// bubbles, where the mention inherits the bubble's own (already compliant) text
// color. The underline carries the distinction that color no longer can, which
// also satisfies 1.4.1 — mentions are no longer identified by color alone.
const MentionSpan = styled('span')<{ isAssistant: boolean; onColoredSurface?: boolean }>(
  ({ isAssistant, onColoredSurface }) => ({
    display: 'inline',
    fontWeight: 700,
    cursor: 'pointer',
    ...(isAssistant && {
      textDecoration: 'underline',
      ...(onColoredSurface
        ? { color: 'inherit' }
        : { color: 'var(--mui-palette-chat-mentionText, #B45309)' }),
    }),
  }),
);

// TEMPORARY: these tenants still see citations and their snippets,
// but must not open the source document in the canvas panel or download it.
const CITATION_SOURCE_BLOCKED_TENANTS = ['flcom', 'capture'];

const isCitationSourceBlocked = () =>
  CITATION_SOURCE_BLOCKED_TENANTS.some(tenant =>
    window.location.origin.toLowerCase().includes(tenant)
  );

const getFileIcon = (fileName: string) => {
  const extension = fileName.toLowerCase().split('.').pop();

  let icon;

  switch (extension) {
    case 'pdf':
      icon = filePdf;
      break;
    case 'docx':
    case 'doc':
      icon = fileDocx;
      break;
    case 'xlsx':
    case 'xls':
      icon = fileXlsx;
      break;
    case 'txt':
      icon = fileTxt;
      break;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
    case 'svg':
    case 'bmp':
      return <ImageIcon style={{ width: '48px', height: '48px', color: '#757575' }} />;
    default:
      icon = fileLogo;
      break;
  }

  return (
    <img
      src={icon}
      alt={`${extension?.toUpperCase() || 'FILE'} file`}
      style={{
        width: '48px',
        height: '48px',
        objectFit: 'contain'
      }}
    />
  );
};

const ContentBlockRenderer: React.FC<ContentBlockRendererProps> = ({
  blocks,
  onFileDownload,
  onFileOpen,
  onCitationView,
  onCitationDownload,
  onMentionClick,
  userMap = {},
  loadingFileName = null,
  isStreaming = false,
  accentColor = '#1976d2',
  onResolveImageUrl,
  onColoredSurface = false,
}) => {
  const [resolvedImageUrls, setResolvedImageUrls] = useState<Record<string, string>>({});
  const resolvingRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!onResolveImageUrl) return;
    blocks.forEach(block => {
      if (block.type !== 'image' || !block.url) return;
      const url = block.url;
      if (resolvedImageUrls[url] || resolvingRef.current.has(url)) return;
      resolvingRef.current.add(url);
      onResolveImageUrl(url)
        .then(resolved => setResolvedImageUrls(prev => ({ ...prev, [url]: resolved })))
        .catch(() => setResolvedImageUrls(prev => ({ ...prev, [url]: url })))
        .finally(() => resolvingRef.current.delete(url));
    });
  // resolvedImageUrls intentionally excluded — resolvingRef guards against duplicate calls
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocks, onResolveImageUrl]);
  // State for link hover preview
  const [linkPreviewAnchor, setLinkPreviewAnchor] = useState<{ top: number; left: number } | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Shared expanded state for all collapsible block types (collapsed by default)
  const [expandedBlocks, setExpandedBlocks] = useState<Set<number>>(new Set());
  // Expanded state for url_citation groups (keyed by the index of the first block in the group)
  const [expandedCitationGroups, setExpandedCitationGroups] = useState<Set<number>>(new Set());

  const canvas = useCanvasPanel();

  const citationSourceBlocked = isCitationSourceBlocked();

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const surfacePalette = (theme.palette as any).surface ?? {};
  const textPrimary    = surfacePalette.primaryText   ?? (isDark ? '#FFFFFF'                 : '#212121');
  const textSecondary  = surfacePalette.secondaryText ?? (isDark ? 'rgba(255,255,255,0.65)' : '#757575');
  const blockBorder    = surfacePalette.divider       ?? (isDark ? 'rgba(255,255,255,0.12)' : '#e0e0e0');
  const blockBg        = isDark ? 'rgba(0,0,0,0.2)'   : '#f5f5f5';
  const blockBgHover   = isDark ? 'rgba(0,0,0,0.3)'   : '#eeeeee';
  const blockBgAlt     = isDark ? 'rgba(0,0,0,0.15)'  : '#f8f9fa';
  const blockBgAltHover = isDark ? 'rgba(0,0,0,0.25)' : '#f0f4f8';
  const codeBg         = isDark ? 'rgba(255,255,255,0.1)' : '#f5f5f5';

  const toggleBlock = (index: number) => {
    setExpandedBlocks(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleCitationGroup = (groupIndex: number) => {
    setExpandedCitationGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupIndex)) next.delete(groupIndex);
      else next.add(groupIndex);
      return next;
    });
  };

  // Handler for citation download
  const handleCitationDownload = (citation: DocumentCitationBlock) => {
    if (onCitationDownload) {
      void onCitationDownload(citation);
      return;
    }
    // Create a file block structure from the citation to reuse the file download logic
    const fileBlock: FileBlock = {
      type: 'file',
      fileName: citation.uri,
      url: citation.sourceId, // Use sourceId as the URL/identifier
      fileType: citation.uri.split('.').pop() || 'document'
    };
    onFileDownload?.(fileBlock);
  };

  // Helper function to extract domain from URL
  const getDomainFromUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.hostname;
    } catch {
      return url;
    }
  };

  // Helper function to get protocol and security status
  const getProtocol = (url: string): { protocol: string; isSecure: boolean } => {
    try {
      const urlObj = new URL(url);
      return {
        protocol: urlObj.protocol.replace(':', ''),
        isSecure: urlObj.protocol === 'https:'
      };
    } catch {
      return { protocol: 'unknown', isSecure: false };
    }
  };

  // Helper function to clean snippet text
  const cleanSnippet = (snippet: string): string => {
    // Remove HTML comments
    let cleaned = snippet.replace(/<!--[\s\S]*?-->/g, '');
    // Remove HTML tags
    cleaned = cleaned.replace(/<[^>]*>/g, '');
    // Replace multiple newlines with a single space
    cleaned = cleaned.replace(/\n+/g, ' ');
    // Replace multiple spaces with a single space
    cleaned = cleaned.replace(/\s+/g, ' ');
    // Trim whitespace
    cleaned = cleaned.trim();
    return cleaned;
  };

  // Opens the preview. Bound to both hover and keyboard focus so the preview
  // is not mouse-only (SyntheticEvent covers MouseEvent and FocusEvent).
  const handleLinkMouseEnter = (event: React.SyntheticEvent<HTMLAnchorElement>, url: string) => {
    if (hoverTimeout) clearTimeout(hoverTimeout);

    // Capture the bounding rect immediately before setTimeout
    const rect = event.currentTarget.getBoundingClientRect();

    const timeout = setTimeout(() => {
      setLinkPreviewAnchor({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
      });
      setPreviewUrl(url);
    }, 300); // 300ms delay prevents flicker on quick mouse passes

    setHoverTimeout(timeout);
  };

  // Event handler for link mouse leave
  const handleLinkMouseLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }

    closeTimeoutRef.current = setTimeout(() => {
      setLinkPreviewAnchor(null);
      setPreviewUrl('');
    }, 300);
  };

  // Event handler for preview close
  const handlePreviewClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setLinkPreviewAnchor(null);
    setPreviewUrl('');
  };

  const renderBlock = (block: StreamableBlock, index: number) => {
    // Skeleton for atomic blocks that have started but not yet received block_data
    if (block._streaming && block.type !== 'text') {
      return (
        <Skeleton
          key={index}
          variant="rectangular"
          width="100%"
          height={60}
          animation="wave"
          sx={{ borderRadius: 1, my: 1 }}
        />
      );
    }

    switch (block.type) {
      case 'text':
        // Extract leading and trailing newlines to preserve them
        const leadingNewlines = block.content.match(/^(\r?\n)+/)?.[0] || '';
        const trailingNewlines = block.content.match(/(\r?\n)+$/)?.[0] || '';
        const trimmedContent = block.content.replace(/^(\r?\n)+/, '').replace(/(\r?\n)+$/, '');

        // Count paragraphs in the content (double newlines indicate paragraph breaks)
        const paragraphCount = (trimmedContent.match(/\n\s*\n/g) || []).length + 1;
        const hasMultipleParagraphs = paragraphCount > 1;

        return (
          <span key={index} style={{ display: 'inline' }}>
            {leadingNewlines && <span style={{ whiteSpace: 'pre' }}>{leadingNewlines}</span>}
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Render paragraphs inline for single paragraph, block for multiple
                p: ({ children }: any) => {
                  if (hasMultipleParagraphs) {
                    // For multiple paragraphs, use block display with proper spacing
                    return <div style={{ whiteSpace: 'pre-wrap', marginBottom: '0.5em', fontSize: '1.2rem'}}>{children}</div>;
                  }
                  // For single paragraph, render inline to flow with mentions
                  return <span style={{ whiteSpace: 'pre-wrap', fontSize: '1.2rem' }}>{children}</span>;
                },
              // Style links
              a: ({ href, children }) => (
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: accentColor,
                    textDecoration: 'none',
                    borderBottom: `1px solid ${accentColor}`,
                    cursor: 'pointer',
                    transition: 'border-color 0.2s ease',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (href) {
                      window.open(href, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  onMouseEnter={(e) => handleLinkMouseEnter(e, href || '')}
                  onMouseLeave={handleLinkMouseLeave}
                  onFocus={(e) => handleLinkMouseEnter(e, href || '')}
                  onBlur={handleLinkMouseLeave}
                  onKeyDown={(e) => {
                    // Dismissable without moving the pointer or focus (WCAG 1.4.13)
                    if (e.key === 'Escape') handlePreviewClose();
                  }}
                >
                  {children}
                </a>
              ),
              // Style code blocks (inline code)
              code: ({ inline, children, ...props }: any) =>
                inline ? (
                  <code
                    style={{
                      backgroundColor: codeBg,
                      color: 'inherit',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      fontSize: '0.9em',
                      fontFamily: 'monospace'
                    }}
                    {...props}
                  >
                    {children}
                  </code>
                ) : (
                  <code {...props}>{children}</code>
                ),
              // Style lists
              ul: ({ children }) => <ul style={{ margin: '0.5em 0', paddingLeft: '1.5em', color: 'inherit' }}>{children}</ul>,
              ol: ({ children }) => <ol style={{ margin: '0.5em 0', paddingLeft: '1.5em', color: 'inherit' }}>{children}</ol>,
              li: ({ children }) => <li style={{ color: 'inherit' }}>{children}</li>,
              strong: ({ children }) => <strong style={{ color: 'inherit' }}>{children}</strong>,
              em: ({ children }) => <em style={{ color: 'inherit' }}>{children}</em>,
              // Style blockquotes
              blockquote: ({ children }) => (
                <blockquote
                  style={{
                    borderLeft: `4px solid ${blockBorder}`,
                    paddingLeft: '1em',
                    margin: '0.5em 0',
                    color: textSecondary,
                    fontStyle: 'italic'
                  }}
                >
                  {children}
                </blockquote>
              ),
              // Style tables
              table: ({ children }) => (
                <table
                  style={{
                    borderCollapse: 'collapse',
                    width: '100%',
                    margin: '0.5em 0'
                  }}
                >
                  {children}
                </table>
              ),
              th: ({ children }) => (
                <th
                  style={{
                    border: `1px solid ${blockBorder}`,
                    padding: '8px',
                    backgroundColor: blockBg,
                    color: textPrimary,
                    textAlign: 'left'
                  }}
                >
                  {children}
                </th>
              ),
              td: ({ children }) => (
                <td
                  style={{
                    border: `1px solid ${blockBorder}`,
                    padding: '8px',
                    color: 'inherit'
                  }}
                >
                  {children}
                </td>
              ),
            }}
          >
            {trimmedContent}
          </ReactMarkdown>
          {trailingNewlines && <span style={{ whiteSpace: 'pre' }}>{trailingNewlines}</span>}
          </span>
        );
      case 'mention':
        const userFromMap = userMap[block.userId] || userMap[block.userId?.toLowerCase()] || userMap[block.userId?.toUpperCase()];
        const isAssistantMention = block.role === 'assistant' || userFromMap?.userType === 'agent';
        return (
          <MentionSpan
            key={index}
            isAssistant={isAssistantMention}
            onColoredSurface={onColoredSurface}
            className={`mention ${isAssistantMention ? 'mention-assistant' : ''}`}
            {...activatable(() => onMentionClick?.(block.userId), `Mention: ${block.displayText?.trim() ?? ''}`)}
          >
            {' '}{block.displayText}
          </MentionSpan>
        );

      case 'code':
        const isCodeExpanded = expandedBlocks.has(index);
        const firstCodeLine = block.content.split('\n').find(l => l.trim()) || '';
        return (
          <div key={index} style={{ margin: '8px 0', borderRadius: '4px', overflow: 'hidden', border: '1px solid #3c3c3c' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 12px',
                backgroundColor: '#2d2d2d',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              {...activatable(() => toggleBlock(index), isCodeExpanded ? 'Collapse code' : 'Expand code')}
            >
              <span style={{ fontSize: '12px', color: '#569cd6', fontFamily: 'monospace', fontWeight: 600, flexShrink: 0 }}>
                {block.language || 'code'}
              </span>
              {!isCodeExpanded && (
                <span style={{
                  fontSize: '12px',
                  color: '#808080',
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}>
                  {firstCodeLine}
                </span>
              )}
              {isCodeExpanded
                ? <ExpandLessIcon style={{ color: '#9e9e9e', fontSize: '18px', marginLeft: 'auto', flexShrink: 0 }} />
                : <ExpandMoreIcon style={{ color: '#9e9e9e', fontSize: '18px', marginLeft: 'auto', flexShrink: 0 }} />
              }
            </div>
            {isCodeExpanded && (
              <SyntaxHighlighter
                language={block.language}
                style={vscDarkPlus}
                customStyle={{ borderRadius: '0', padding: '12px', fontSize: '14px', margin: 0 }}
              >
                {block.content}
              </SyntaxHighlighter>
            )}
          </div>
        );

      case 'image': {
        const resolvedSrc = block.url ? resolvedImageUrls[block.url] : undefined;
        return (
          <div key={index} style={{ margin: '8px 0' }}>
            {resolvedSrc ? (
              <img
                src={resolvedSrc}
                alt={block.altText}
                style={{ maxWidth: '100%', borderRadius: '4px', display: 'block' }}
              />
            ) : (
              <Skeleton variant="rectangular" width={200} height={150} animation="wave" sx={{ borderRadius: 1 }} />
            )}
          </div>
        );
      }

      case 'file':
        const isLoading = loadingFileName === block.fileName;
        return (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- role, tabIndex and onKeyDown are supplied by the activatable() spread below; jsx-a11y cannot see through a spread and reports a false positive here.
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '12px 16px',
              backgroundColor: blockBg,
              borderRadius: '8px',
              border: `1px solid ${blockBorder}`,
              margin: '8px 0',
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = blockBgHover;
              e.currentTarget.style.borderColor = blockBorder;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = blockBg;
              e.currentTarget.style.borderColor = blockBorder;
            }}
            {...activatable(() => onFileOpen?.(block), `Open file ${block.fileName}`)}
          >
            <div style={{ flexShrink: 0 }} aria-hidden="true">
              {getFileIcon(block.fileName)}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '14px',
                fontWeight: 500,
                color: textPrimary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {block.fileName}
              </div>
              <div style={{
                fontSize: '12px',
                color: textSecondary,
                marginTop: '2px'
              }}>
                Click to open
              </div>
            </div>
            {isLoading ? (
              <CircularProgress
                size={24}
                style={{
                  color: '#1976d2',
                  flexShrink: 0
                }}
              />
            ) : (
              <DownloadIcon
                style={{
                  color: '#1976d2',
                  fontSize: '24px',
                  flexShrink: 0,
                  cursor: 'pointer',
                }}
                {...activatable((e) => {
                  e.stopPropagation();
                  onFileDownload?.(block);
                }, `Download ${block.fileName}`)}
              />
            )}
          </div>
        );

      case 'url_citation':
        return (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- as above: activatable() supplies role/tabIndex/onKeyDown via spread.
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '10px 14px',
              backgroundColor: blockBgAlt,
              borderRadius: '6px',
              border: `1px solid ${blockBorder}`,
              borderLeft: `3px solid ${accentColor}`,
              marginLeft: '28px',
              marginTop: '6px',
              marginBottom: '6px',
              transition: 'all 0.2s ease',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = blockBgAltHover;
              e.currentTarget.style.borderColor = blockBorder;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = blockBgAlt;
              e.currentTarget.style.borderColor = blockBorder;
            }}
            {...activatable(() => {
              if (!block.data?.url) return;
              const url: string = block.data.url;
              const isPdf = /\.pdf(\?.*)?$/i.test(url);
              const isImage = /\.(png|jpe?g|gif|svg|webp)(\?.*)?$/i.test(url);
              if (isPdf || isImage) {
                canvas.loadDocument({
                  uri: url,
                  title: block.data?.title || getDomainFromUrl(url),
                  mimeType: isPdf ? 'application/pdf' : undefined,
                });
              } else {
                window.open(url, '_blank', 'noopener,noreferrer');
              }
            }, `Open citation: ${block.data?.title || (block.data?.url ? getDomainFromUrl(block.data.url) : 'link')}`)}
          >
            <div style={{ flexShrink: 0, marginTop: '2px' }}>
              <FormatQuoteIcon
                style={{
                  color: accentColor,
                  fontSize: '24px'
                }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: '14px',
                fontWeight: 600,
                color: textPrimary,
                marginBottom: '4px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {block.data?.title}
              </div>
              <div style={{
                fontSize: '12px',
                color: accentColor,
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {getDomainFromUrl(block.data?.url || '')}
                </span>
                <OpenInNewIcon style={{ fontSize: '14px' }} />
              </div>
            </div>
          </div>
        );

      case 'citation':
        const isCitationLoading = loadingFileName === block.uri;
        const isCitationExpanded = expandedBlocks.has(index);
        return (
          <div
            key={index}
            style={{
              backgroundColor: blockBgAlt,
              borderRadius: '6px',
              border: `1px solid ${blockBorder}`,
              borderLeft: `3px solid ${accentColor}`,
              marginLeft: '28px',
              marginTop: '6px',
              marginBottom: '6px',
              overflow: 'hidden',
              opacity: isCitationLoading ? 0.7 : 1,
            }}
          >
            {/* Header — always visible. Opens the document in canvas, or just
                toggles the snippet when source access is blocked. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '8px 14px',
                cursor: 'pointer',
                userSelect: 'none',
              }}
              {...activatable((e) => {
                e.stopPropagation();
                if (citationSourceBlocked) {
                  toggleBlock(index);
                  return;
                }
                if (!isCitationLoading) onCitationView?.(block);
              }, citationSourceBlocked
                ? (isCitationExpanded ? 'Collapse citation details' : 'Expand citation details')
                : `View citation: ${block.title}`)}
            >
              <div style={{
                flexShrink: 0,
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: accentColor,
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                fontWeight: 700,
              }}>
                {block.citationIndex}
              </div>
              <div style={{
                flex: 1,
                fontSize: '14px',
                fontWeight: 600,
                color: textPrimary,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {block.title}
              </div>
              {isCitationExpanded
                ? <ExpandLessIcon style={{ color: '#9e9e9e', fontSize: '18px', flexShrink: 0 }} {...activatable((e) => { e.stopPropagation(); toggleBlock(index); }, 'Collapse citation details')} />
                : <ExpandMoreIcon style={{ color: '#9e9e9e', fontSize: '18px', flexShrink: 0 }} {...activatable((e) => { e.stopPropagation(); toggleBlock(index); }, 'Expand citation details')} />
              }
            </div>

            {/* Expanded content: snippet + download footer */}
            {isCitationExpanded && (
              <div style={{ padding: '0 14px 10px 48px' }}>
                {block.snippet && (
                  <div style={{
                    fontSize: '12px',
                    color: textSecondary,
                    lineHeight: '1.5',
                    marginBottom: '8px',
                    display: '-webkit-box',
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {cleanSnippet(block.snippet)}
                  </div>
                )}
                {/* Layout row only. The click affordance sits on the URI below
                    rather than on this row, because the row also contains the
                    download control — making the row a button would nest one
                    interactive element inside another (axe nested-interactive)
                    and leave the download unreachable by keyboard. */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                    fontSize: '11px',
                  }}
                >
                  <div
                    style={{
                      color: citationSourceBlocked ? textSecondary : accentColor,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      flex: 1,
                      overflow: 'hidden',
                      cursor: isCitationLoading || citationSourceBlocked ? 'default' : 'pointer',
                    }}
                    {...(isCitationLoading || citationSourceBlocked
                      ? {}
                      : activatable((e) => {
                          e.stopPropagation();
                          onCitationView?.(block);
                        }, `View citation ${block.uri}`))}
                  >

                    {isCitationLoading ? (
                      <>
                        <CircularProgress size={12} style={{ color: accentColor }} />
                        <span>Downloading...</span>
                      </>
                    ) : (
                      <>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{block.uri}</span>
                      </>
                    )}
                  </div>
                  {/* Download is a separate action — hidden entirely for tenants
                      whose citation source access is blocked */}
                  {!isCitationLoading && !citationSourceBlocked && (
                    <DownloadIcon
                      style={{ fontSize: '13px', flexShrink: 0, color: accentColor }}
                      {...activatable((e) => { e.stopPropagation(); handleCitationDownload(block); }, `Download ${block.title}`)}
                    />
                  )}
                  {block.relevanceScore > 0 && (
                    <span style={{ color: accentColor, backgroundColor: `${accentColor}14`, padding: '2px 6px', borderRadius: '4px', fontWeight: 600, flexShrink: 0 }}>
                      {(block.relevanceScore * 100).toFixed(1)}%
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 'web_search':
        const isSearchExpanded = expandedBlocks.has(index);
        return (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: isSearchExpanded ? '10px 14px' : '5px 10px',
              backgroundColor: isSearchExpanded ? `${accentColor}12` : blockBg,
              borderRadius: '8px',
              border: `1px solid ${isSearchExpanded ? `${accentColor}40` : blockBorder}`,
              margin: '4px 0',
              cursor: 'pointer',
              userSelect: 'none',
              transition: 'all 0.2s ease',
            }}
            {...activatable(() => toggleBlock(index), isSearchExpanded ? 'Collapse web search' : 'Expand web search')}
          >
            <SearchIcon style={{ color: accentColor, fontSize: isSearchExpanded ? '20px' : '16px', flexShrink: 0 }} />
            {isSearchExpanded
              ? <>
                  <span style={{ fontSize: '13px', color: textSecondary }}>Searched the web:</span>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: textPrimary, flex: 1 }}>"{block.query}"</span>
                </>
              : <span style={{ fontSize: '12px', color: textSecondary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {block.query}
                </span>
            }
            {isSearchExpanded
              ? <ExpandLessIcon style={{ color: '#9e9e9e', fontSize: '16px', flexShrink: 0 }} />
              : <ExpandMoreIcon style={{ color: '#9e9e9e', fontSize: '16px', flexShrink: 0 }} />
            }
          </div>
        );

      case 'reasoning_summary':
        const isExpanded = expandedBlocks.has(index);
        const toggleExpanded = () => toggleBlock(index);

        // Extract title from the text (first line if it starts with **)
        const textContent = block.text || '';
        const titleMatch = textContent.match(/^\*\*(.+?)\*\*/);
        const title = titleMatch ? titleMatch[1] : 'Thinking';
        const bodyContent = titleMatch
          ? textContent.replace(/^\*\*.+?\*\*\n*/, '').trim()
          : textContent;

        return (
          <div
            key={index}
            style={{
              margin: '8px 0',
              backgroundColor: blockBg,
              borderRadius: '6px',
              border: `1px solid ${blockBorder}`,
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                backgroundColor: blockBgHover,
                cursor: 'pointer',
                userSelect: 'none'
              }}
              {...activatable(toggleExpanded, isExpanded ? `Collapse ${title}` : `Expand ${title}`)}
            >
              <PsychologyIcon style={{ color: textSecondary, fontSize: '18px' }} />
              <span style={{ fontSize: '12px', fontWeight: 500, color: textSecondary, flex: 1 }}>
                {title}...
              </span>
              {isExpanded ? (
                <ExpandLessIcon style={{ color: textSecondary, fontSize: '18px' }} />
              ) : (
                <ExpandMoreIcon style={{ color: textSecondary, fontSize: '18px' }} />
              )}
            </div>
            {isExpanded && bodyContent && (
              <div style={{
                padding: '10px 12px',
                fontSize: '12px',
                color: textSecondary,
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap'
              }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {bodyContent}
                </ReactMarkdown>
              </div>
            )}
          </div>
        );

      case 'stdout':
        return (
          <div key={index} style={{ margin: '8px 0' }}>
            <div style={{
              backgroundColor: '#1e1e1e',
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '4px 10px',
                backgroundColor: '#2d2d2d',
                fontSize: '11px',
                color: '#9e9e9e',
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                letterSpacing: '0.05em'
              }}>
                {block.stream}
              </div>
              <pre style={{
                margin: 0,
                padding: '10px 12px',
                fontSize: '13px',
                color: '#d4d4d4',
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                lineHeight: 1.5
              }}>
                {block.logs}
              </pre>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Render consecutive url_citation blocks as a single collapsible "N sources" group
  const renderUrlCitationGroup = (group: ContentBlock[], groupStartIndex: number) => {
    const isGroupExpanded = expandedCitationGroups.has(groupStartIndex);
    const count = group.length;
    return (
      <div key={`url-citation-group-${groupStartIndex}`} style={{ margin: '4px 0' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            backgroundColor: `${accentColor}0d`,
            border: `1px solid ${accentColor}40`,
            borderRadius: '16px',
            cursor: 'pointer',
            userSelect: 'none',
          }}
          {...activatable(() => toggleCitationGroup(groupStartIndex), `${isGroupExpanded ? 'Collapse' : 'Expand'} ${count} ${count === 1 ? 'source' : 'sources'}`)}
        >
          <LinkIcon style={{ fontSize: '14px', color: accentColor }} />
          <span style={{ fontSize: '12px', fontWeight: 600, color: accentColor }}>
            {count} {count === 1 ? 'source' : 'sources'}
          </span>
          {isGroupExpanded
            ? <ExpandLessIcon style={{ fontSize: '14px', color: accentColor }} />
            : <ExpandMoreIcon style={{ fontSize: '14px', color: accentColor }} />
          }
        </div>
        {isGroupExpanded && (
          <div style={{ marginTop: '4px' }}>
            {group.map((block, i) => renderBlock(block, groupStartIndex + i))}
          </div>
        )}
      </div>
    );
  };

  // Render consecutive citation (DocumentCitation) blocks as a single collapsible "N citations" group
  const renderDocumentCitationGroup = (group: ContentBlock[], groupStartIndex: number) => {
    const isGroupExpanded = expandedCitationGroups.has(groupStartIndex);
    const count = group.length;
    return (
      <div key={`doc-citation-group-${groupStartIndex}`} style={{ margin: '4px 0 4px 28px' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            backgroundColor: `${accentColor}0d`,
            border: `1px solid ${accentColor}40`,
            borderLeft: `3px solid ${accentColor}`,
            borderRadius: '16px',
            cursor: 'pointer',
            userSelect: 'none',
          }}
          {...activatable(() => toggleCitationGroup(groupStartIndex), `${isGroupExpanded ? 'Collapse' : 'Expand'} ${count} ${count === 1 ? 'citation' : 'citations'}`)}
        >
          <FormatQuoteIcon style={{ fontSize: '14px', color: accentColor }} />
          <span style={{ fontSize: '12px', fontWeight: 600, color: accentColor }}>
            {count} {count === 1 ? 'citation' : 'citations'}
          </span>
          {isGroupExpanded
            ? <ExpandLessIcon style={{ fontSize: '14px', color: accentColor }} />
            : <ExpandMoreIcon style={{ fontSize: '14px', color: accentColor }} />
          }
        </div>
        {isGroupExpanded && (
          <div style={{ marginTop: '4px' }}>
            {group.map((block, i) => renderBlock(block, groupStartIndex + i))}
          </div>
        )}
      </div>
    );
  };

  const renderAllBlocks = () => {
    const result: React.ReactNode[] = [];
    let i = 0;
    const streamableBlocks = blocks as StreamableBlock[];
    while (i < streamableBlocks.length) {
      const block = streamableBlocks[i];
      if (block.type === 'url_citation') {
        const groupStart = i;
        const group: ContentBlock[] = [];
        while (i < streamableBlocks.length && streamableBlocks[i].type === 'url_citation') {
          group.push(streamableBlocks[i]);
          i++;
        }
        result.push(renderUrlCitationGroup(group, groupStart));
      } else if (block.type === 'citation') {
        const groupStart = i;
        const group: ContentBlock[] = [];
        while (i < streamableBlocks.length && streamableBlocks[i].type === 'citation') {
          group.push(streamableBlocks[i]);
          i++;
        }
        result.push(renderDocumentCitationGroup(group, groupStart));
      } else {
        result.push(renderBlock(block, i));
        i++;
      }
    }

    if (isStreaming) {
      result.push(
        <React.Fragment key="streaming-cursor">
          <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0} }`}</style>
          <span
            // Purely decorative caret. It carried aria-label="streaming", which
            // put a stray "streaming" into the message text on every render;
            // the streaming state is conveyed by the TypingIndicator instead.
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: 2,
              height: '1em',
              backgroundColor: 'currentColor',
              marginLeft: 2,
              verticalAlign: 'text-bottom',
              animation: 'blink 0.8s step-end infinite',
            }}
          />
        </React.Fragment>
      );
    }

    return result;
  };

  return (
    <>
      <div className="content-blocks">
        {renderAllBlocks()}
      </div>

      {/* Link Preview Popover */}
      <Popover
        open={Boolean(linkPreviewAnchor) && Boolean(previewUrl)}
        anchorReference="anchorPosition"
        anchorPosition={linkPreviewAnchor || { top: 0, left: 0 }}
        onClose={handlePreviewClose}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            // The preview only duplicates the link the user already has, and it
            // embeds a live third-party iframe. Keeping it out of the
            // accessibility tree and the tab order avoids a focus trap while
            // losing no information — the link itself remains operable.
            'aria-hidden': true,
            sx: {
              maxWidth: 500,
              minWidth: 400,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: 2,
              pointerEvents: 'auto',
            },
            onMouseEnter: () => {
              if (hoverTimeout) {
                clearTimeout(hoverTimeout);
                setHoverTimeout(null);
              }
              if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
              }
            },
            onMouseLeave: handlePreviewClose,
          },
        }}
        sx={{
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ p: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <LinkIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Link Preview
            </Typography>
            {getProtocol(previewUrl).isSecure && (
              <LockIcon sx={{ fontSize: 14, color: 'success.main', ml: 'auto' }} />
            )}
          </Box>

          {/* Domain */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              DOMAIN
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: 'primary.main',
                wordBreak: 'break-all',
              }}
            >
              {getDomainFromUrl(previewUrl)}
            </Typography>
          </Box>

          {/* URL */}
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              URL
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontSize: '0.85rem',
                wordBreak: 'break-all',
                maxHeight: 60,
                overflow: 'auto',
              }}
            >
              <a
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
              >
                {previewUrl}
              </a>
            </Typography>
          </Box>

          {/* Protocol Chip */}
          <Chip
            label={getProtocol(previewUrl).protocol.toUpperCase()}
            size="small"
            color={getProtocol(previewUrl).isSecure ? 'success' : 'default'}
            sx={{
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 600,
              mb: 2,
            }}
          />

          {/* Divider */}
          <Box sx={{ borderTop: '1px solid #e0e0e0', mb: 2 }} />

          {/* Page Preview Section */}
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', display: 'block', mb: 1 }}>
              PAGE PREVIEW
            </Typography>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 300,
                backgroundColor: '#f5f5f5',
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid #e0e0e0',
              }}
            >
              <iframe
                src={previewUrl}
                title="Page Preview"
                tabIndex={-1}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                }}
                sandbox="allow-scripts allow-same-origin"
              />
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  backgroundColor: 'rgba(0, 0, 0, 0.6)',
                  color: 'white',
                  padding: '2px 6px',
                  borderRadius: 1,
                  fontSize: '0.65rem',
                }}
              >
                Preview may be blocked by some sites
              </Typography>
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ContentBlockRenderer;
